/**
 * DiffusionTab — section "Diffusion" de la page Réglages (PR3 — 2026-05-10).
 *
 * Successeur de `<ConnectionsTab>` enrichi : sous chaque carte plateforme,
 * trois sections collapsibles (Compte / Cadence / Fenêtres). L'utilisatrice
 * configure tout en un seul endroit.
 *
 * Le tab key URL `?tab=connections` est **conservé** pour rétro-compat
 * avec le callback Etsy OAuth qui redirige sur ce param. Le label affiché
 * dans `<SettingsPage>` passe à "Diffusion".
 */

import { useEffect } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import EmptyState from '@/components/ui/EmptyState'
import PlatformConnectionCard from '@/components/platforms/PlatformConnectionCard'
import PlatformPacingCard from '@/components/platforms/PlatformPacingCard'
import { usePlatformConnections } from '@/hooks/usePlatformConnections'
import {
  PLATFORM_IS_IMPLEMENTED,
  PLATFORM_LABEL_FR,
  type Platform,
} from '@/types/platform-connections'

import './DiffusionTab.css'

const PLATFORMS_ORDER: Platform[] = [
  'etsy',
  'pinterest',
  'instagram',
  'tiktok',
  'saatchi',
  'artfinder',
]

const ETSY_CALLBACK_MESSAGES: Record<string, { kind: 'success' | 'error' | 'info'; msg: string }> =
  {
    ok: { kind: 'success', msg: `${PLATFORM_LABEL_FR.etsy} connecté.` },
    denied: { kind: 'info', msg: 'Connexion annulée — aucun changement.' },
    failed: {
      kind: 'error',
      msg: 'Connexion Etsy échouée. Réessaye dans quelques instants.',
    },
    invalid: {
      kind: 'error',
      msg: 'Réponse Etsy invalide. Recommence depuis le bouton « Connecter ».',
    },
  }

function useEtsyCallbackBanner(): void {
  const [params, setParams] = useSearchParams()
  useEffect(() => {
    const status = params.get('etsy')
    if (!status) return
    const entry = ETSY_CALLBACK_MESSAGES[status]
    if (entry) {
      if (entry.kind === 'success') toast.success(entry.msg)
      else if (entry.kind === 'error') toast.error(entry.msg)
      else toast.info(entry.msg)
    }
    const next = new URLSearchParams(params)
    next.delete('etsy')
    setParams(next, { replace: true })
  }, [params, setParams])
}

export default function DiffusionTab() {
  useEtsyCallbackBanner()
  const connectionsQuery = usePlatformConnections()

  if (connectionsQuery.isLoading) {
    return (
      <p className="ui-body ui-text-muted" role="status">
        Chargement des connexions plateformes…
      </p>
    )
  }

  if (connectionsQuery.error) {
    return (
      <EmptyState
        title="Impossible de charger les connexions"
        description={String((connectionsQuery.error as Error).message ?? 'Erreur réseau.')}
      />
    )
  }

  const connections = connectionsQuery.data ?? []

  return (
    <section className="diffusion-tab" aria-label="Diffusion">
      <p className="ui-body ui-text-muted diffusion-tab-intro">
        Configure ici la connexion plateforme + la cadence anti-bot + les fenêtres horaires de
        publication. Les tokens d&apos;accès sont chiffrés (Fernet) avant stockage. La cadence et
        les fenêtres s&apos;appliquent dès qu&apos;un adapter consomme la config — tu peux
        configurer en avance les plateformes pas encore livrées.
      </p>

      <div className="diffusion-tab-stack">
        {PLATFORMS_ORDER.map((platform) => {
          const connection = connections.find((c) => c.platform === platform) ?? null
          const platformLabel = PLATFORM_LABEL_FR[platform] ?? platform
          return (
            <article key={platform} className="diffusion-tab-platform">
              <header className="diffusion-tab-platform-header">
                <h2 className="ui-h3 diffusion-tab-platform-title">{platformLabel}</h2>
                {!PLATFORM_IS_IMPLEMENTED[platform] ? (
                  <span className="ui-body-sm ui-text-muted">Bientôt disponible</span>
                ) : null}
              </header>

              <details className="diffusion-tab-section" open>
                <summary className="diffusion-tab-section-summary">Compte</summary>
                <div className="diffusion-tab-section-body">
                  <PlatformConnectionCard
                    platform={platform}
                    isImplemented={PLATFORM_IS_IMPLEMENTED[platform]}
                    connection={connection}
                  />
                </div>
              </details>

              <details className="diffusion-tab-section">
                <summary className="diffusion-tab-section-summary">Cadence anti-bot</summary>
                <div className="diffusion-tab-section-body">
                  <PlatformPacingCard platform={platform} platformLabel={platformLabel} />
                </div>
              </details>
            </article>
          )
        })}
      </div>
    </section>
  )
}
